import {
  fnGetKmlActionsFromProps,
  fnGetKmzActionGroupsFromProps
} from './portal-action-state.js'
import {
  resolveKmzPlaybackUseGlobalHeading,
  resolveKmzPlaybackLocalHeadingField,
  resolveKmzPlaybackGlobalHeadingField
} from './portal-kmz-heading-utils.js'
import {
  resolveKmzPlaybackSpeedInfo
} from './portal-kmz-speed-utils.js'

const SHADOW_CENTER_KEY = 'K'
const SHADOW_COLOR_BY_KEY = {
  B: '#0d6efd',
  K: '#6f42c1',
  J: '#dc3545'
}

const fnNormalizeRoute3dActionParams = function (paramsSrc) {
  const src = (paramsSrc && typeof paramsSrc === 'object') ? paramsSrc : {}
  const out = {}
  Object.keys(src)
    .filter((key) => String(key || '').trim())
    .sort((a, b) => String(a).localeCompare(String(b)))
    .forEach((key) => {
      out[String(key)] = String(src[key] === undefined || src[key] === null ? '' : src[key])
    })
  return out
}

const fnNormalizeRoute3dActionEntry = function (entry) {
  if (!entry || typeof entry !== 'object') return null
  const actuator = String(entry.actuator || entry.text || '').trim()
  if (!actuator) return null
  return {
    actuator: actuator,
    params: fnNormalizeRoute3dActionParams(entry.params)
  }
}

const fnNormalizeRoute3dActionTrigger = function (triggerSrc) {
  const src = (triggerSrc && typeof triggerSrc === 'object') ? triggerSrc : {}
  const out = {}
  Object.keys(src)
    .filter((key) => String(key || '').trim())
    .sort((a, b) => String(a).localeCompare(String(b)))
    .forEach((key) => {
      out[String(key)] = String(src[key] === undefined || src[key] === null ? '' : src[key])
    })
  if (!String(out.actionTriggerType || '').trim()) {
    out.actionTriggerType = 'reachPoint'
  }
  return out
}

const fnNormalizeRoute3dActionGroups = function (groupsSrc, fallbackPointIndex) {
  const fallbackIndex = Number.isFinite(Number(fallbackPointIndex)) ? Number(fallbackPointIndex) : 0
  return (Array.isArray(groupsSrc) ? groupsSrc : [])
    .map((group, groupIdx) => {
      if (!group || typeof group !== 'object') return null
      const actions = (Array.isArray(group.actions) ? group.actions : [])
        .map(fnNormalizeRoute3dActionEntry)
        .filter((item) => !!item)
      if (!actions.length) return null
      return {
        actionGroupId: String(group.actionGroupId === undefined || group.actionGroupId === null ? (fallbackIndex + groupIdx) : group.actionGroupId),
        actionGroupStartIndex: String(group.actionGroupStartIndex === undefined || group.actionGroupStartIndex === null ? fallbackIndex : group.actionGroupStartIndex),
        actionGroupEndIndex: String(group.actionGroupEndIndex === undefined || group.actionGroupEndIndex === null ? fallbackIndex : group.actionGroupEndIndex),
        actionGroupMode: String(group.actionGroupMode || 'sequence').trim() || 'sequence',
        actionTrigger: fnNormalizeRoute3dActionTrigger(group.actionTrigger),
        actions: actions
      }
    })
    .filter((group) => !!group)
}

const fnBuildRoute3dKmlActionGroups = function (point) {
  const actions = fnGetKmlActionsFromProps(point && point.props)
  const pointIndex = Number.isFinite(Number(point && point.idx)) ? Number(point.idx) : 0
  if (!actions.length) return []
  return fnNormalizeRoute3dActionGroups([
    {
      actionGroupId: pointIndex,
      actionGroupStartIndex: pointIndex,
      actionGroupEndIndex: pointIndex,
      actionGroupMode: 'sequence',
      actionTrigger: {
        actionTriggerType: 'reachPoint'
      },
      actions: actions.map((action) => ({
        actuator: action && action.text,
        params: (action && action.param) ? { param: action.param } : {}
      }))
    }
  ], pointIndex)
}

const fnGetRoute3dActionGroupsFromPoint = function (point, missionType) {
  const type = String(missionType || '').trim().toLowerCase()
  if (type === 'kmz') {
    return fnNormalizeRoute3dActionGroups(
      fnGetKmzActionGroupsFromProps(point && point.props, point),
      Number(point && point.idx)
    )
  }
  return fnBuildRoute3dKmlActionGroups(point)
}

const fnFlattenRoute3dActionsFromGroups = function (groups) {
  return (Array.isArray(groups) ? groups : []).flatMap((group) => {
    return Array.isArray(group && group.actions) ? group.actions : []
  })
}

const fnBuildRoute3dActionDigest = function (groups) {
  try {
    return JSON.stringify(fnNormalizeRoute3dActionGroups(groups, 0))
  } catch (_error) {
    return '[]'
  }
}

const createRoute3dSyncController = function ({
  _ = window._,
  fnT = function (key) { return String(key || '') },
  toFiniteNumber: fnToFiniteNumber = function (value) {
    const num = Number(value)
    return Number.isFinite(num) ? num : null
  },
  fnGetMissionState = function () { return null },
  fnGetMissionFileName = function () { return '' },
  fnGetMissionType = function () { return 'kml' },
  fnGetMissionAltitudeContextFor3d = function () { return {} },
  fnGetPreferredPointDemValue = function () { return null },
  fnGetPointAltitudeFor3d = function () { return null },
  fnGetPointRawHeightValue = function () { return null },
  fnGetPointName = function () { return '' },
  fnGetPointDescription = function () { return '' },
  Swal = window.Swal,
  windowRef = window,
  idbKeyval = (windowRef ? windowRef.idbKeyval : null),
  ROUTE3D_SHARED_KEY = 'portal-route-3d-current-v1',
  ROUTE3D_SYNC_EVENT_KEY = 'portal-route-3d-sync',
  ROUTE3D_BROADCAST_CHANNEL = 'portal-route-3d',
  ROUTE3D_VIEWER_WINDOW_NAME = 'portal-route-3d-viewer',
  ROUTE3D_SYNC_DELAY_MS = 140,
} = {}) {

  const LOCAL_TERRAIN_STATE_KEY = 'PORTAL_LOCAL_TERRAIN_STATE'
  const LOCAL_DEM_PROVIDER_KEY = 'localDEM'
  const DEFAULT_DEM_PROVIDER_KEY = 'gDEM'

  let route3dBroadcastChannel = null
  let route3dSyncTimer = null
  let route3dLastSavedSignature = null
  let route3dLastSavedSignatureReady = false
  let route3dViewerWindowRef = null
  let route3dLatestScheduledSyncId = 0
  const route3dDemCache = new Map()
  const fnIsRoute3dSyncSuspended = function () {
    const suspendCount = Number(windowRef && windowRef.PORTAL_ROUTE3D_SYNC_SUSPEND_COUNT)
    return Number.isFinite(suspendCount) && suspendCount > 0
  }

  const fnGetPath = function (obj, path) {
    if (_ && typeof _.get === 'function') {
      return _.get(obj, path)
    }
    const parts = String(path || '').split('.').filter(Boolean)
    let current = obj
    for (let i = 0; i < parts.length; i++) {
      if (!current || typeof current !== 'object') return undefined
      current = current[parts[i]]
    }
    return current
  }

  const fnNormalizeHeadingMode = function (value, fallbackValue) {
    const raw = String(value || '').trim()
    if (!raw) return String(fallbackValue || 'followWayline')
    const normalized = raw.toLowerCase()
    if (normalized === 'followwayline') return 'followWayline'
    if (normalized === 'manually') return 'manually'
    if (normalized === 'fixed') return 'fixed'
    if (normalized === 'smoothtransition') return 'smoothTransition'
    if (normalized === 'towardpoi') return 'towardPOI'
    return String(fallbackValue || 'followWayline')
  }

  const fnNormalizeHeadingPathMode = function (value, fallbackValue) {
    const raw = String(value || '').trim()
    if (!raw) return String(fallbackValue || 'followBadArc')
    const normalized = raw.toLowerCase()
    if (normalized === 'clockwise') return 'clockwise'
    if (normalized === 'counterclockwise') return 'counterClockwise'
    if (normalized === 'followbadarc') return 'followBadArc'
    return String(fallbackValue || 'followBadArc')
  }

  const fnParseBoolLike = function (value) {
    if (value === null || value === undefined) return null
    if (typeof value === 'boolean') return value
    const text = String(value).trim().toLowerCase()
    if (!text) return null
    if (text === '1' || text === 'true' || text === 'yes' || text === 'on') return true
    if (text === '0' || text === 'false' || text === 'no' || text === 'off') return false
    const numeric = Number(text)
    if (Number.isFinite(numeric)) return numeric !== 0
    return null
  }

  const fnReadFirstPresent = function (obj, pathList) {
    const paths = Array.isArray(pathList) ? pathList : []
    for (let i = 0; i < paths.length; i++) {
      const value = fnGetPath(obj, paths[i])
      if (value === undefined || value === null) continue
      if (typeof value === 'string' && !String(value).trim()) continue
      return value
    }
    return null
  }

  const fnParseHeadingPoiPoint = function (value) {
    const text = String(value || '').trim()
    if (!text) return null
    const parts = text.split(',').map(function (part) {
      return Number(String(part).trim())
    })
    if (parts.length < 2) return null
    const first = Number(parts[0])
    const second = Number(parts[1])
    const third = Number(parts[2])
    if (!Number.isFinite(first) || !Number.isFinite(second)) return null
    let lng = first
    let lat = second
    if (Math.abs(first) <= 90 && Math.abs(second) > 90) {
      lat = first
      lng = second
    }
    return {
      lat: lat,
      lng: lng,
      alt: Number.isFinite(third) ? third : 0
    }
  }

  const fnBuildEffectiveHeadingParam = function (point, missionType) {
    const props = fnGetPath(point, 'props') || {}
    const missionTypeLower = String(missionType || '').trim().toLowerCase()
    const isKmzMission = missionTypeLower === 'kmz'
    const fnResolveHeadingBlock = function (modeName) {
      const fieldResolver = (modeName === 'global')
        ? (isKmzMission ? resolveKmzPlaybackGlobalHeadingField : function (_point, childName, fallbackValue) {
            return fallbackValue === undefined ? null : fallbackValue
          })
        : (isKmzMission ? resolveKmzPlaybackLocalHeadingField : function (_point, childName, fallbackValue) {
            return fnReadFirstPresent(props, [
              'wpml:waypointHeadingParam.wpml:' + childName,
              'waypointHeadingParam.' + childName
            ]) ?? (fallbackValue === undefined ? null : fallbackValue)
          })
      const modeRaw = fieldResolver(point, 'waypointHeadingMode', null)
      const mode = fnNormalizeHeadingMode(modeRaw, '')
      const angle = fnToFiniteNumber(fieldResolver(point, 'waypointHeadingAngle', null))
      const angleEnableRaw = fnParseBoolLike(fieldResolver(point, 'waypointHeadingAngleEnable', null))
      const pathModeRaw = fieldResolver(point, 'waypointHeadingPathMode', null)
      const poiRaw = fieldResolver(point, 'waypointPoiPoint', null)
      const pathFallback = mode === 'smoothTransition' ? 'followBadArc' : 'clockwise'
      const poiIndexRaw = fnToFiniteNumber(fieldResolver(point, 'waypointHeadingPoiIndex', null))
      return {
        hasExplicit: modeRaw !== null || angle !== null || angleEnableRaw !== null || pathModeRaw !== null || poiRaw !== null || poiIndexRaw !== null,
        mode: mode || 'followWayline',
        angle: angle,
        angleEnabled: angleEnableRaw === null ? (mode === 'smoothTransition' && angle !== null) : angleEnableRaw,
        pathMode: fnNormalizeHeadingPathMode(pathModeRaw, pathFallback),
        poi: fnParseHeadingPoiPoint(poiRaw),
        poiIndex: poiIndexRaw === null ? null : Math.max(0, Math.round(poiIndexRaw))
      }
    }

    const useGlobal = fnParseBoolLike(
      isKmzMission
        ? resolveKmzPlaybackUseGlobalHeading(point, null)
        : fnReadFirstPresent(props, [
            'wpml:useGlobalHeadingParam',
            'useGlobalHeadingParam'
          ])
    ) === true
    const headingBlock = fnResolveHeadingBlock(useGlobal ? 'global' : 'local')
    const legacyHeading = fnToFiniteNumber(fnReadFirstPresent(props, [
      'ExtendedData.mis:heading',
      'mis:heading'
    ]))
    const hasSpecificHeading = headingBlock.hasExplicit === true

    if (hasSpecificHeading) {
      return {
        useGlobal: useGlobal,
        source: useGlobal ? 'global' : 'local',
        mode: fnNormalizeHeadingMode(headingBlock.mode, 'followWayline'),
        angle: headingBlock.angle,
        angleEnabled: headingBlock.angleEnabled === true,
        pathMode: fnNormalizeHeadingPathMode(headingBlock.pathMode, headingBlock.mode === 'smoothTransition' ? 'followBadArc' : 'clockwise'),
        poi: headingBlock.poi,
        poiIndex: headingBlock.poiIndex
      }
    }

    if (legacyHeading !== null) {
      return {
        useGlobal: false,
        source: 'legacy',
        mode: 'smoothTransition',
        angle: legacyHeading,
        angleEnabled: true,
        pathMode: 'followBadArc',
        poi: null,
        poiIndex: null
      }
    }

    return {
      useGlobal: useGlobal,
      source: 'default',
      mode: 'followWayline',
      angle: null,
      angleEnabled: false,
      pathMode: 'clockwise',
      poi: null,
      poiIndex: null
    }
  }

  const fnBuildRoute3dHeadingDigest = function (headingParam) {
    const src = (headingParam && typeof headingParam === 'object') ? headingParam : {}
    const poi = (src.poi && typeof src.poi === 'object') ? src.poi : null
    try {
      return JSON.stringify({
        useGlobal: src.useGlobal === true,
        source: String(src.source || ''),
        mode: String(src.mode || 'followWayline'),
        angle: fnToFiniteNumber(src.angle),
        angleEnabled: src.angleEnabled === true,
        pathMode: String(src.pathMode || ''),
        poi: poi
          ? {
              lat: fnToFiniteNumber(poi.lat),
              lng: fnToFiniteNumber(poi.lng),
              alt: fnToFiniteNumber(poi.alt)
            }
          : null,
        poiIndex: fnToFiniteNumber(src.poiIndex)
      })
    } catch (_error) {
      return '{}'
    }
  }

  const fnNormalizeRoute3dSpeedScope = function (scopeSrc) {
    const src = (scopeSrc && typeof scopeSrc === 'object') ? scopeSrc : {}
    return {
      useGlobalSpeed: src.useGlobalSpeed === true ? true : (src.useGlobalSpeed === false ? false : null),
      waypointSpeed: fnToFiniteNumber(src.waypointSpeed),
      autoFlightSpeed: fnToFiniteNumber(src.autoFlightSpeed)
    }
  }

  const fnBuildRoute3dSpeedDigest = function (speedInfoSrc) {
    const src = (speedInfoSrc && typeof speedInfoSrc === 'object') ? speedInfoSrc : {}
    const resolved = (src.resolved && typeof src.resolved === 'object') ? src.resolved : {}
    try {
      return JSON.stringify({
        unit: String(src.unit || 'm/s'),
        resolver: String(src.resolver || ''),
        scopePriority: (Array.isArray(src.scopePriority) ? src.scopePriority : []).map(function (entry) {
          return String(entry || '')
        }),
        scopes: {
          template: fnNormalizeRoute3dSpeedScope(src.scopes && src.scopes.template),
          waylines: fnNormalizeRoute3dSpeedScope(src.scopes && src.scopes.waylines)
        },
        resolved: {
          useGlobalSpeed: resolved.useGlobalSpeed === true ? true : (resolved.useGlobalSpeed === false ? false : null),
          useGlobalSpeedScope: String(resolved.useGlobalSpeedScope || ''),
          localSpeed: fnToFiniteNumber(resolved.localSpeed),
          localSpeedScope: String(resolved.localSpeedScope || ''),
          globalSpeed: fnToFiniteNumber(resolved.globalSpeed),
          globalSpeedScope: String(resolved.globalSpeedScope || ''),
          speedMps: fnToFiniteNumber(resolved.speedMps),
          speedScope: String(resolved.speedScope || ''),
          speedField: String(resolved.speedField || ''),
          decisionMode: String(resolved.decisionMode || '')
        }
      })
    } catch (_error) {
      return '{}'
    }
  }

  const fnBuildDemCacheKey = function (providerKey, lat, lng) {
    const key = String(providerKey || '').trim()
    const latNum = Number(lat)
    const lngNum = Number(lng)
    if (!key || !Number.isFinite(latNum) || !Number.isFinite(lngNum)) return ''
    const latRounded = Math.round(latNum * 100000) / 100000
    const lngRounded = Math.round(lngNum * 100000) / 100000
    return key + ':' + latRounded.toFixed(5) + ',' + lngRounded.toFixed(5)
  }

  const fnGetCachedDem = function (providerKey, lat, lng) {
    const cacheKey = fnBuildDemCacheKey(providerKey, lat, lng)
    if (!cacheKey || !route3dDemCache.has(cacheKey)) return null
    const value = Number(route3dDemCache.get(cacheKey))
    return Number.isFinite(value) ? value : null
  }

  const fnSetCachedDem = function (providerKey, lat, lng, value) {
    const cacheKey = fnBuildDemCacheKey(providerKey, lat, lng)
    const n = Number(value)
    if (!cacheKey || !Number.isFinite(n)) return
    route3dDemCache.set(cacheKey, Math.round(n * 10) / 10)
  }

  const fnFetchDemElevationsByProvider = async function (providerKey, pointsPayload) {
    const key = String(providerKey || '').trim()
    const points = Array.isArray(pointsPayload) ? pointsPayload : []
    if (!key || !points.length) return []
    const fetchImpl = (windowRef && typeof windowRef.fetch === 'function')
      ? windowRef.fetch.bind(windowRef)
      : (typeof fetch === 'function' ? fetch : null)
    if (typeof fetchImpl !== 'function') {
      throw new Error(fnT('dem.request_failed'))
    }
    const response = await fetchImpl('/api/dem-elevation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        provider: key,
        points: points.map(function (pt) {
          return { lat: Number(pt.lat), lng: Number(pt.lng) }
        })
      })
    })
    const payload = await response.json().catch(function () { return null })
    if (!response.ok) {
      const message = String((payload && payload.error) || ('DEM proxy hiba: HTTP ' + response.status))
      throw new Error(message)
    }
    const results = Array.isArray(payload && payload.results) ? payload.results : []
    return points.map(function (_pt, idx) {
      const row = results[idx]
      const elevation = Number(row && row.elevation)
      return Number.isFinite(elevation) ? elevation : null
    })
  }

  const buildPayloadFromMission = async function () {
    const missionState = fnGetMissionState()
    if (!missionState || !Array.isArray(missionState.waypoints) || missionState.waypoints.length < 2) {
      return null
    }
    const fnGetLocalTerrainState = function () {
      if (!windowRef || typeof windowRef !== 'object') return null
      return windowRef[LOCAL_TERRAIN_STATE_KEY] || null
    }
    const fnGetDemProviderLabelSafe = function (providerKey) {
      const key = String(providerKey || '').trim()
      if (!key) return DEFAULT_DEM_PROVIDER_KEY
      if (windowRef && typeof windowRef.PORTAL_GET_DEM_PROVIDER_LABEL === 'function') {
        const label = String(windowRef.PORTAL_GET_DEM_PROVIDER_LABEL(key) || '').trim()
        if (label) return label
      }
      if (key === LOCAL_DEM_PROVIDER_KEY) return 'Local DEM/DSM'
      return key
    }
    const fnGetPrimaryDemProviderKey = function () {
      const raw = (windowRef && typeof windowRef.PORTAL_GET_MAP_CURSOR_ELEVATION_PROVIDER === 'function')
        ? windowRef.PORTAL_GET_MAP_CURSOR_ELEVATION_PROVIDER()
        : DEFAULT_DEM_PROVIDER_KEY
      const key = String(raw || '').trim()
      return key || DEFAULT_DEM_PROVIDER_KEY
    }
    const fnGetFallbackDemProviderKey = function (primaryProviderKey) {
      const primary = String(primaryProviderKey || '').trim() || DEFAULT_DEM_PROVIDER_KEY
      const raw = (windowRef && typeof windowRef.PORTAL_GET_MAP_CURSOR_ELEVATION_FALLBACK_PROVIDER === 'function')
        ? windowRef.PORTAL_GET_MAP_CURSOR_ELEVATION_FALLBACK_PROVIDER()
        : ''
      const key = String(raw || '').trim()
      if (key && key !== LOCAL_DEM_PROVIDER_KEY) return key
      if (primary !== LOCAL_DEM_PROVIDER_KEY) return primary
      return DEFAULT_DEM_PROVIDER_KEY
    }
    const fnHasLocalTerrainSampler = function (localState) {
      return !!(
        localState &&
        localState.active === true &&
        (
          typeof localState.sampleElevation === 'function' ||
          typeof localState.sampleElevationAsync === 'function' ||
          typeof localState.sampleElevations === 'function'
        )
      )
    }
    const fnSampleLocalDemAt = function (lat, lng) {
      const localState = fnGetLocalTerrainState()
      if (!fnHasLocalTerrainSampler(localState) || typeof localState.sampleElevation !== 'function') return null
      const latNum = Number(lat)
      const lngNum = Number(lng)
      if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null
      const sampled = Number(localState.sampleElevation(latNum, lngNum))
      return Number.isFinite(sampled) ? sampled : null
    }
    const fnSampleLocalDemBatch = async function (points) {
      const localState = fnGetLocalTerrainState()
      const rows = Array.isArray(points) ? points : []
      if (!fnHasLocalTerrainSampler(localState) || !rows.length) {
        return rows.map(function () { return null })
      }
      try {
        if (typeof localState.sampleElevations === 'function') {
          const sampledValues = await localState.sampleElevations(rows, { resampling: 'nearest' })
          return rows.map(function (_row, idx) {
            const sampled = Number(sampledValues[idx])
            return Number.isFinite(sampled) ? sampled : null
          })
        }
        if (typeof localState.sampleElevationAsync === 'function') {
          return await Promise.all(rows.map(async function (row) {
            const latNum = Number(row && row.lat)
            const lngNum = Number(row && row.lng)
            if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null
            const sampled = Number(await localState.sampleElevationAsync(latNum, lngNum, { resampling: 'nearest' }))
            return Number.isFinite(sampled) ? sampled : null
          }))
        }
        if (typeof localState.sampleElevation === 'function') {
          return rows.map(function (row) {
            const latNum = Number(row && row.lat)
            const lngNum = Number(row && row.lng)
            if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null
            const sampled = Number(localState.sampleElevation(latNum, lngNum))
            return Number.isFinite(sampled) ? sampled : null
          })
        }
      } catch (_error) {
        return rows.map(function () { return null })
      }
      return rows.map(function () { return null })
    }
    const fnCollectPointDemValues = function (pt, lat, lng) {
      const out = {}
      const rawValues = fnGetPath(pt, 'props.__demValues')
      if (rawValues && typeof rawValues === 'object') {
        Object.keys(rawValues).forEach((keyRaw) => {
          const key = String(keyRaw || '').trim()
          if (!key) return
          const val = Number(rawValues[keyRaw])
          if (!Number.isFinite(val)) return
          out[key] = val
        })
      }
      const sampledLocal = fnSampleLocalDemAt(lat, lng)
      if (Number.isFinite(sampledLocal)) {
        out[LOCAL_DEM_PROVIDER_KEY] = sampledLocal
      }
      return out
    }
    const fnResolvePointDemByProvider = function (demValues, providerKey, fallbackValue) {
      const key = String(providerKey || '').trim()
      if (key && demValues && Object.prototype.hasOwnProperty.call(demValues, key)) {
        const val = Number(demValues[key])
        if (Number.isFinite(val)) return val
      }
      const fb = Number(fallbackValue)
      if (Number.isFinite(fb)) return fb
      return null
    }
    const fnPickFinite = function (values) {
      for (let i = 0; i < values.length; i++) {
        const n = Number(values[i])
        if (Number.isFinite(n)) return n
      }
      return null
    }
    const fnResolveAltitudeRefType = function (modeRaw) {
      const mode = String(modeRaw || '').trim()
      if (mode === 'EGM96') return 'geoidLike'
      if (mode === 'WGS84') return 'absoluteEllipsoid'
      if (mode === 'relativeToStartPoint') return 'relativeToStartPoint'
      if (mode === 'realTimeFollowSurface') return 'relativeToSurface'
      return 'absoluteEllipsoid'
    }
    const fnReadPointFinite = function (pt, paths) {
      for (let i = 0; i < paths.length; i++) {
        const value = fnToFiniteNumber(fnGetPath(pt, paths[i]))
        if (value !== null) return value
      }
      return null
    }
    const missionAltContext = fnGetMissionAltitudeContextFor3d(missionState)
    const shadowActiveKeyRaw = String(fnGetPath(missionState, '__shadowBundle.activeKey') || SHADOW_CENTER_KEY).trim().toUpperCase()
    const shadowActiveKey = Object.prototype.hasOwnProperty.call(SHADOW_COLOR_BY_KEY, shadowActiveKeyRaw)
      ? shadowActiveKeyRaw
      : SHADOW_CENTER_KEY
    const activeRouteColor = SHADOW_COLOR_BY_KEY[shadowActiveKey] || SHADOW_COLOR_BY_KEY[SHADOW_CENTER_KEY]
    const primaryDemProviderKey = fnGetPrimaryDemProviderKey()
    const fallbackDemProviderKey = fnGetFallbackDemProviderKey(primaryDemProviderKey)
    const templateAltitudeRefType = fnResolveAltitudeRefType(missionAltContext && missionAltContext.heightMode)
    const executeAltitudeRefType = fnResolveAltitudeRefType(missionAltContext && missionAltContext.executeHeightMode)
    const fnResolvePointSurfaceAbsoluteFromDem = function (pt, demMeters) {
      const dem = fnToFiniteNumber(demMeters)
      if (dem === null || !pt) return null
      const ellipsoidHeight = fnReadPointFinite(pt, [
        'props.wpml:ellipsoidHeight',
        'props.ellipsoidHeight',
        'props.template.wpml:ellipsoidHeight',
        'props.waylines.wpml:ellipsoidHeight',
        'altitude',
        'props.coordinates.alt',
        'props.Point.coordinates.alt'
      ])
      const pointHeight = fnReadPointFinite(pt, [
        'props.Point.height',
        'props.wpml:height',
        'props.height',
        'props.template.wpml:height',
        'props.waylines.wpml:height'
      ])
      const executeHeight = fnReadPointFinite(pt, [
        'props.Point.executeHeight',
        'props.wpml:executeHeight',
        'props.executeHeight',
        'props.template.wpml:executeHeight',
        'props.waylines.wpml:executeHeight'
      ])
      const cachedOffset = fnReadPointFinite(pt, ['props.__egm96Offset'])

      if (ellipsoidHeight !== null && pointHeight !== null && templateAltitudeRefType === 'relativeToSurface') {
        return ellipsoidHeight - pointHeight
      }
      if (ellipsoidHeight !== null && executeHeight !== null && executeAltitudeRefType === 'relativeToSurface') {
        return ellipsoidHeight - executeHeight
      }
      if (ellipsoidHeight !== null && pointHeight !== null && templateAltitudeRefType === 'geoidLike') {
        return dem + (ellipsoidHeight - pointHeight)
      }
      if (ellipsoidHeight !== null && executeHeight !== null && executeAltitudeRefType === 'geoidLike') {
        return dem + (ellipsoidHeight - executeHeight)
      }
      if (cachedOffset !== null) {
        return dem + cachedOffset
      }
      return null
    }
    const fnMedian = function (values) {
      const list = (Array.isArray(values) ? values : [])
        .map(function (value) { return Number(value) })
        .filter(function (value) { return Number.isFinite(value) })
        .sort(function (a, b) { return a - b })
      if (!list.length) return null
      const middle = Math.floor(list.length / 2)
      if (list.length % 2 === 1) return list[middle]
      return (list[middle - 1] + list[middle]) / 2
    }
    const fnBuildKmlPointSpeedInfo = function (pt) {
      const props = fnGetPath(pt, 'props') || {}
      const directSpeedMps = fnToFiniteNumber(fnGetPath(props, 'speedMps'))
      const directSpeed = fnToFiniteNumber(fnGetPath(props, 'speed'))
      const resolvedSpeedMps = fnPickFinite([directSpeedMps, directSpeed])
      return {
        unit: 'm/s',
        resolver: 'kmlPoint',
        scopePriority: [],
        scopes: {
          template: {
            useGlobalSpeed: null,
            waypointSpeed: null,
            autoFlightSpeed: null
          },
          waylines: {
            useGlobalSpeed: null,
            waypointSpeed: null,
            autoFlightSpeed: null
          }
        },
        resolved: {
          useGlobalSpeed: null,
          useGlobalSpeedScope: '',
          localSpeed: null,
          localSpeedScope: '',
          globalSpeed: null,
          globalSpeedScope: '',
          speedMps: resolvedSpeedMps,
          speedScope: resolvedSpeedMps !== null ? 'point' : 'default',
          speedField: directSpeedMps !== null ? 'speedMps' : (directSpeed !== null ? 'speed' : ''),
          decisionMode: directSpeedMps !== null ? 'directSpeedMps' : (directSpeed !== null ? 'directSpeed' : 'default')
        }
      }
    }

    const fnBuildPointSpeedInfo = function (pt) {
      if (missionType === 'kmz') {
        return resolveKmzPlaybackSpeedInfo(pt)
      }
      return fnBuildKmlPointSpeedInfo(pt)
    }
    const fnApplyProviderDemValues = async function (providerKey, pointRows) {
      const key = String(providerKey || '').trim()
      const rows = Array.isArray(pointRows) ? pointRows : []
      if (!key || !rows.length) return
      if (key === LOCAL_DEM_PROVIDER_KEY) {
        const sampledValues = await fnSampleLocalDemBatch(rows.map(function (row) {
          return {
            lat: Number(row && row.lat),
            lng: Number(row && row.lng)
          }
        }))
        rows.forEach((row, idx) => {
          const sampledLocal = Number(sampledValues[idx])
          if (!Number.isFinite(sampledLocal)) return
          const rounded = Math.round(Number(sampledLocal) * 10) / 10
          row.demByProvider[key] = rounded
          fnSetCachedDem(key, row.lat, row.lng, rounded)
        })
        return
      }
      const fetchIndexes = []
      const fetchPoints = []
      rows.forEach((row, idx) => {
        const cached = fnGetCachedDem(key, row.lat, row.lng)
        if (Number.isFinite(cached)) {
          row.demByProvider[key] = cached
          return
        }
        delete row.demByProvider[key]
        fetchIndexes.push(idx)
        fetchPoints.push({ lat: Number(row.lat), lng: Number(row.lng) })
      })
      if (!fetchPoints.length) return
      try {
        const elevations = await fnFetchDemElevationsByProvider(key, fetchPoints)
        fetchIndexes.forEach((rowIdx, idx) => {
          const row = rows[rowIdx]
          if (!row) return
          const elevation = Number(elevations[idx])
          if (!Number.isFinite(elevation)) return
          const rounded = Math.round(elevation * 10) / 10
          row.demByProvider[key] = rounded
          fnSetCachedDem(key, row.lat, row.lng, rounded)
        })
      } catch (_error) {}
    }
    const missionTypeRaw = String(fnGetMissionType() || '').trim().toLowerCase()
    const missionType = missionTypeRaw === 'kmz' ? 'kmz' : 'kml'
    const pointRows = missionState.waypoints
      .map((pt, index) => {
        const lat = Number(pt && pt.latlng && pt.latlng[0])
        const lng = Number(pt && pt.latlng && pt.latlng[1])
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
        const demByProvider = fnCollectPointDemValues(pt, lat, lng)
        return {
          pt: pt,
          index: index + 1,
          lat: lat,
          lng: lng,
          demByProvider: demByProvider,
          preferredDem: fnGetPreferredPointDemValue(pt)
        }
      })
      .filter(Boolean)
    if (pointRows.length < 2) return null

    await fnApplyProviderDemValues(primaryDemProviderKey, pointRows)
    if (fallbackDemProviderKey !== primaryDemProviderKey) {
      await fnApplyProviderDemValues(fallbackDemProviderKey, pointRows)
    }

    const projectSurfaceOffset = fnMedian(pointRows.map(function (row) {
      const referenceDem = fnPickFinite([
        row && row.preferredDem,
        row && row.demByProvider && row.demByProvider[primaryDemProviderKey],
        row && row.demByProvider && row.demByProvider[fallbackDemProviderKey]
      ])
      const resolvedSurface = fnResolvePointSurfaceAbsoluteFromDem(row && row.pt, referenceDem)
      return (resolvedSurface !== null && referenceDem !== null) ? (resolvedSurface - referenceDem) : null
    }))
    const fnResolveSurfaceAbsoluteHeight = function (pt, demMeters) {
      const dem = fnToFiniteNumber(demMeters)
      if (dem === null) return null
      const pointSpecific = fnResolvePointSurfaceAbsoluteFromDem(pt, dem)
      if (pointSpecific !== null) return pointSpecific
      if (projectSurfaceOffset !== null) return dem + projectSurfaceOffset
      return dem
    }

    const points = pointRows
      .map((row) => {
        const pt = row.pt
        const actionGroups = fnGetRoute3dActionGroupsFromPoint(pt, missionType)
        const headingParam = fnBuildEffectiveHeadingParam(pt, missionType)
        const speedInfo = fnBuildPointSpeedInfo(pt)
        const demPrimary = fnResolvePointDemByProvider(row.demByProvider, primaryDemProviderKey, null)
        const demFallback = fnResolvePointDemByProvider(row.demByProvider, fallbackDemProviderKey, null)
        const dem = fnPickFinite([demPrimary, demFallback, row.preferredDem])
        const demPrimaryAbsolute = fnResolveSurfaceAbsoluteHeight(pt, demPrimary)
        const demFallbackAbsolute = fnResolveSurfaceAbsoluteHeight(pt, demFallback)
        const demAbsolute = fnPickFinite([
          demPrimaryAbsolute,
          demFallbackAbsolute,
          fnResolveSurfaceAbsoluteHeight(pt, row.preferredDem)
        ])
        const altitudeMeta = fnGetPointAltitudeFor3d(pt, dem, missionAltContext, { withMeta: true })
        const altitudeResolved = fnToFiniteNumber(altitudeMeta && altitudeMeta.altitude)
        const rawHeightFallback = fnGetPointRawHeightValue(pt)
        const altitudeFromProps = fnToFiniteNumber(
          fnGetPath(pt, 'props.Point.executeHeight')
            ?? fnGetPath(pt, 'props.wpml:executeHeight')
            ?? fnGetPath(pt, 'props.executeHeight')
            ?? fnGetPath(pt, 'props.Point.height')
            ?? fnGetPath(pt, 'props.wpml:height')
            ?? fnGetPath(pt, 'props.height')
        )
        const altitude = (altitudeResolved !== null)
          ? altitudeResolved
          : ((rawHeightFallback !== null) ? rawHeightFallback : altitudeFromProps)
        return {
          index: row.index,
          name: fnGetPointName(pt) || ('WP ' + row.index),
          description: fnGetPointDescription(pt),
          lat: row.lat,
          lng: row.lng,
          altitude: (altitude !== null) ? altitude : null,
          altitudeRefType: String((altitudeMeta && altitudeMeta.refType) || ''),
          altitudeNeedsStartReference: !!(altitudeMeta && altitudeMeta.needsStartReference === true),
          dem: Number.isFinite(dem) ? dem : null,
          demPrimary: Number.isFinite(demPrimary) ? demPrimary : null,
          demFallback: Number.isFinite(demFallback) ? demFallback : null,
          demAbsolute: Number.isFinite(demAbsolute) ? demAbsolute : null,
          demPrimaryAbsolute: Number.isFinite(demPrimaryAbsolute) ? demPrimaryAbsolute : null,
          demFallbackAbsolute: Number.isFinite(demFallbackAbsolute) ? demFallbackAbsolute : null,
          demByProvider: row.demByProvider,
          speedMps: fnToFiniteNumber(speedInfo && speedInfo.resolved && speedInfo.resolved.speedMps),
          speedInfo: speedInfo,
          headingParam: headingParam,
          actionGroups: actionGroups,
          actions: fnFlattenRoute3dActionsFromGroups(actionGroups)
        }
      })
      .filter(Boolean)
    if (points.length < 2) return null
    const missionSpeedMps = fnPickFinite(points.map((pt) => pt.speedMps))
    return {
      missionName: fnGetMissionFileName() || 'mission',
      missionType: missionType,
      missionHeightMode: String((missionAltContext && missionAltContext.heightMode) || ''),
      missionExecuteHeightMode: String((missionAltContext && missionAltContext.executeHeightMode) || ''),
      missionTakeoffEllipsoidHeight: fnToFiniteNumber(missionAltContext && missionAltContext.takeoffEllipsoidHeight),
      missionSpeedMps: missionSpeedMps,
      routeColors: {
        flight: activeRouteColor,
        terrain: activeRouteColor
      },
      demProviders: {
        primary: primaryDemProviderKey,
        fallback: fallbackDemProviderKey,
        primaryLabel: fnGetDemProviderLabelSafe(primaryDemProviderKey),
        fallbackLabel: fnGetDemProviderLabelSafe(fallbackDemProviderKey)
      },
      generatedAtIso: new Date().toISOString(),
      points: points
    }
  }

  const buildPayloadSignature = function (payload) {
    if (!payload || !Array.isArray(payload.points) || payload.points.length < 2) return 'null'
    const safe = {
      missionName: String(payload.missionName || 'mission'),
      missionType: String(payload.missionType || 'kml'),
      missionHeightMode: String(payload.missionHeightMode || ''),
      missionExecuteHeightMode: String(payload.missionExecuteHeightMode || ''),
      missionTakeoffEllipsoidHeight: Number.isFinite(Number(payload.missionTakeoffEllipsoidHeight)) ? Number(payload.missionTakeoffEllipsoidHeight) : null,
      missionSpeedMps: Number.isFinite(Number(payload.missionSpeedMps)) ? Number(payload.missionSpeedMps) : null,
      routeColors: {
        flight: String(fnGetPath(payload, 'routeColors.flight') || ''),
        terrain: String(fnGetPath(payload, 'routeColors.terrain') || '')
      },
      demProviders: {
        primary: String(fnGetPath(payload, 'demProviders.primary') || ''),
        fallback: String(fnGetPath(payload, 'demProviders.fallback') || ''),
        primaryLabel: String(fnGetPath(payload, 'demProviders.primaryLabel') || ''),
        fallbackLabel: String(fnGetPath(payload, 'demProviders.fallbackLabel') || '')
      },
      points: payload.points.map((pt, idx) => ({
        index: Number.isFinite(Number(pt && pt.index)) ? Number(pt.index) : (idx + 1),
        name: String((pt && pt.name) || ('WP ' + (idx + 1))),
        lat: Number.isFinite(Number(pt && pt.lat)) ? Number(pt.lat) : null,
        lng: Number.isFinite(Number(pt && pt.lng)) ? Number(pt.lng) : null,
        altitude: Number.isFinite(Number(pt && pt.altitude)) ? Number(pt.altitude) : null,
        altitudeRefType: String((pt && pt.altitudeRefType) || ''),
        altitudeNeedsStartReference: !!(pt && pt.altitudeNeedsStartReference === true),
        dem: Number.isFinite(Number(pt && pt.dem)) ? Number(pt.dem) : null,
        demPrimary: Number.isFinite(Number(pt && pt.demPrimary)) ? Number(pt.demPrimary) : null,
        demFallback: Number.isFinite(Number(pt && pt.demFallback)) ? Number(pt.demFallback) : null,
        demAbsolute: Number.isFinite(Number(pt && pt.demAbsolute)) ? Number(pt.demAbsolute) : null,
        demPrimaryAbsolute: Number.isFinite(Number(pt && pt.demPrimaryAbsolute)) ? Number(pt.demPrimaryAbsolute) : null,
        demFallbackAbsolute: Number.isFinite(Number(pt && pt.demFallbackAbsolute)) ? Number(pt.demFallbackAbsolute) : null,
        speedMps: Number.isFinite(Number(pt && pt.speedMps)) ? Number(pt.speedMps) : null,
        speedDigest: fnBuildRoute3dSpeedDigest(pt && pt.speedInfo),
        headingDigest: fnBuildRoute3dHeadingDigest(pt && pt.headingParam),
        actionsCount: Array.isArray(pt && pt.actions) ? pt.actions.length : 0,
        actionDigest: fnBuildRoute3dActionDigest(pt && pt.actionGroups)
      }))
    }
    try {
      return JSON.stringify(safe)
    } catch (_error) {
      return 'null'
    }
  }

  const fnGetRoute3dBroadcastChannel = function () {
    if (route3dBroadcastChannel || !windowRef || typeof windowRef.BroadcastChannel === 'undefined') return route3dBroadcastChannel
    try {
      route3dBroadcastChannel = new windowRef.BroadcastChannel(ROUTE3D_BROADCAST_CHANNEL)
    } catch (_error) {
      route3dBroadcastChannel = null
    }
    return route3dBroadcastChannel
  }

  const fnNotifyRoute3dUpdated = function () {
    const channel = fnGetRoute3dBroadcastChannel()
    if (channel && typeof channel.postMessage === 'function') {
      try {
        channel.postMessage({ type: 'route-updated', at: Date.now() })
      } catch (_error) {}
    }
    try {
      if (windowRef && windowRef.localStorage) {
        windowRef.localStorage.setItem(ROUTE3D_SYNC_EVENT_KEY, String(Date.now()))
      }
    } catch (_error) {}
  }

  const savePayloadToIndexedDb = async function (payload) {
    if (!idbKeyval || typeof idbKeyval.set !== 'function') return false
    const nextSignature = buildPayloadSignature(payload)
    try {
      if (!route3dLastSavedSignatureReady) {
        const currentPayload = (typeof idbKeyval.get === 'function') ? await idbKeyval.get(ROUTE3D_SHARED_KEY) : null
        route3dLastSavedSignature = buildPayloadSignature(currentPayload)
        route3dLastSavedSignatureReady = true
      }
      if (route3dLastSavedSignature === nextSignature) return false
      await idbKeyval.set(ROUTE3D_SHARED_KEY, payload || null)
      route3dLastSavedSignature = nextSignature
      route3dLastSavedSignatureReady = true
      fnNotifyRoute3dUpdated()
      return true
    } catch (error) {
      console.error(fnT('log.route3d_indexeddb_write_error'), error)
      return false
    }
  }

  const syncSharedState = async function (options) {
    const opts = (options && typeof options === 'object') ? options : {}
    let syncId = Number(opts.syncId)
    if (!Number.isFinite(syncId)) {
      route3dLatestScheduledSyncId += 1
      syncId = route3dLatestScheduledSyncId
      if (route3dSyncTimer) {
        clearTimeout(route3dSyncTimer)
        route3dSyncTimer = null
      }
    }
    if (fnIsRoute3dSyncSuspended()) {
      return false
    }
    const payload = await buildPayloadFromMission()
    if (fnIsRoute3dSyncSuspended()) {
      return false
    }
    if (Number.isFinite(syncId) && syncId !== route3dLatestScheduledSyncId) {
      return false
    }
    await savePayloadToIndexedDb(payload || null)
    return true
  }

  const scheduleSharedSync = function () {
    if (fnIsRoute3dSyncSuspended()) {
      return false
    }
    route3dLatestScheduledSyncId += 1
    const scheduledSyncId = route3dLatestScheduledSyncId
    if (route3dSyncTimer) {
      clearTimeout(route3dSyncTimer)
    }
    route3dSyncTimer = setTimeout(function () {
      route3dSyncTimer = null
      void syncSharedState({ syncId: scheduledSyncId })
    }, ROUTE3D_SYNC_DELAY_MS)
    return true
  }

  const openViewer = async function () {
    const payload = await buildPayloadFromMission()
    if (!payload) {
      if (Swal && typeof Swal.fire === 'function') {
        Swal.fire(fnT('route3d.need_two_points'))
      }
      return false
    }
    await savePayloadToIndexedDb(payload)
    if (!windowRef || typeof windowRef.open !== 'function') return true
    const opened = windowRef.open('/route-3d-viewer', ROUTE3D_VIEWER_WINDOW_NAME)
    if (opened) {
      route3dViewerWindowRef = opened
      try {
        opened.focus()
      } catch (_error) {}
    }
    return true
  }

  return {
    buildPayloadFromMission: buildPayloadFromMission,
    buildPayloadSignature: buildPayloadSignature,
    savePayloadToIndexedDb: savePayloadToIndexedDb,
    syncSharedState: syncSharedState,
    scheduleSharedSync: scheduleSharedSync,
    openViewer: openViewer,
    getViewerWindowRef: function () { return route3dViewerWindowRef }
  }
}

export {
  createRoute3dSyncController
}
